import FullPageSpinner from '@/components/feedback/FullPageSpinner'
import { BidOverviewDrawerContextProvider } from '@/features/bidding/contexts/BidOverviewDrawerContextProvider'
import { useBidOfferGroupParams } from '@/features/bidding/hooks/useBidOfferGroupParams'
import { useBidOfferPriceData_Aggregated } from '@/features/bidding/hooks/useBidOfferPriceData_Aggregated'
import { useBidOfferPriceData_ByActivationGroup } from '@/features/bidding/hooks/useBidOfferPriceData_ByActivationGroup'
import { useBidOfferPriceDataRequirements } from '@/features/bidding/hooks/useBidOfferPriceDataRequirements'
import BidOfferPriceAggregated from '@/features/bidding/pages/BidOfferPriceAggregated'
import BidOfferPriceByActivationGroup from '@/features/bidding/pages/BidOfferPriceByActivationGroup'
import type { GroupedBidParams } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'

/**
 * Wrapper component that determines which price flow to use based on market program.
 * Routes to either aggregated or by-activation-group flow.
 */
const BidOfferPriceWrapper = () => {
  const groupParamsResult = useBidOfferGroupParams()

  if (groupParamsResult.isLoading) {
    return <FullPageSpinner />
  }

  const groupParams = groupParamsResult.data
  const requirements = useBidOfferPriceDataRequirements(groupParams)

  if (requirements.requiresPriceByActivationGroup) {
    return <BidOfferPriceByActivationGroupWrapper groupParams={groupParams} />
  }

  return <BidOfferPriceAggregatedWrapper groupParams={groupParams} />
}

/**
 * Sub-component for aggregated price flow.
 * Uses flow-specific hook that returns non-nullable aggregated bid data.
 */
function BidOfferPriceAggregatedWrapper({ groupParams }: Readonly<{ groupParams: GroupedBidParams }>) {
  const result = useBidOfferPriceData_Aggregated(groupParams)

  if (result.isLoading) {
    return <FullPageSpinner />
  }

  const { bidData } = result.data

  return (
    <BidOverviewDrawerContextProvider>
      <BidOfferPriceAggregated
        draftBidsMatchingActivationGroups={bidData.draftBidsMatchingActivationGroups}
        groupParams={groupParams}
        initialAggregatedBid={bidData.initialAggregatedBid}
      />
    </BidOverviewDrawerContextProvider>
  )
}

/**
 * Sub-component for by-activation-group price flow.
 * Uses flow-specific hook that returns non-nullable per-group bid data.
 */
function BidOfferPriceByActivationGroupWrapper({ groupParams }: Readonly<{ groupParams: GroupedBidParams }>) {
  const result = useBidOfferPriceData_ByActivationGroup(groupParams)

  if (result.isLoading) {
    return <FullPageSpinner />
  }

  const { bidData } = result.data

  return (
    <BidOverviewDrawerContextProvider>
      <BidOfferPriceByActivationGroup
        bidHistoriesPage={bidData.bidHistoriesPage}
        bidsWithPrices={bidData.bidsWithPrices}
        groupParams={groupParams}
        initialBidsPerActivationGroup={bidData.initialBidsPerActivationGroup}
        latestAcceptedBidsByActivationGroup={bidData.latestAcceptedBidsByActivationGroup}
        latestNonDraftBidsByActivationGroup={bidData.latestNonDraftBidsByActivationGroup}
      />
    </BidOverviewDrawerContextProvider>
  )
}

export default BidOfferPriceWrapper
